import { default as clsx } from 'clsx';
import { CardPassThroughOptions, Card as PrimeCard } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import {
    Dispatch,
    forwardRef,
    type ReactNode,
    Ref,
    SetStateAction,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { truncateString } from '../../_utils/truncate-string';
import { Severity } from '../../models';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { LoadingBlur } from '../LoadingBlur/LoadingBlur';
import { Menu, MenuItem, MenuProps } from '../Menu/Menu';

export interface CardProps {
    wrapperClassName?: string;
    cardClassName?: string;
    className?: string;
    title?: string | ReactNode;
    titleHint?: string;
    actions?: MenuItem[] | ReactNode;
    children?: ReactNode;
    foldable?: boolean;
    defaultFolded?: boolean;
    size?: 'sm';
    severity?: Severity;
    header?: string | ReactNode;
    nativeHeader?: ReactNode;
    onClick?: () => void;
    contrast?: boolean;
    menuProps?: MenuProps;
    subTitle?: string;
    small?: boolean;
    dropShadow?: boolean;
    pt?: CardPassThroughOptions;
    showCollapseButton?: boolean;
    loading?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
}

export interface CardRef {
    setIsExpanded: Dispatch<SetStateAction<boolean>>;
}

function CardComponent(props: CardProps, ref: Ref<CardRef>): ReactNode {
    const {
        cardClassName,
        className,
        title: titleProps,
        titleHint,
        actions,
        children,
        defaultFolded,
        size,
        severity,
        header,
        onClick,
        contrast,
        menuProps,
        subTitle,
        nativeHeader,
        small,
        pt,
        dropShadow,
        showCollapseButton,
        loading,
        wrapperClassName,
        onExpandedChange,
    } = props;

    const [isExpanded, setIsExpanded] = useState(!defaultFolded);
    const canFold = props.foldable ?? true;
    const onExpandedChangeRef = useRef(onExpandedChange);
    onExpandedChangeRef.current = onExpandedChange;
    const isFirstRenderRef = useRef(true);

    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        onExpandedChangeRef.current?.(isExpanded);
    }, [isExpanded]);

    useImperativeHandle(ref, () => ({
        setIsExpanded,
    }));

    const title =
        typeof titleProps === 'string'
            ? truncateString(titleProps, 50)
            : titleProps;
    const cardClass = clsx(
        small ? 'small' : null,
        !isExpanded ? 'content-hidden' : null,
        cardClassName,
        size,
        severity,
        header ? 'has-custom-header' : null,
        contrast ? 'contrast' : null,
        !children ? 'empty-body' : null,
        dropShadow ? 'drop-shadow' : null,
        onClick ? 'clickable' : null,
        'h-full',
    );
    const contentClass = clsx('h-full', className);
    const cardHeaderClass = clsx('card-custom-header', severity);
    const collapsibleButtonClass = clsx(isExpanded ? 'expanded' : null);
    const actionsNode = actions ? (
        Array.isArray(actions) ? (
            <Menu {...menuProps} items={actions} />
        ) : (
            actions
        )
    ) : null;

    const textCardTitle = (
        <div className="flex flex-row justify-content-between align-items-center">
            <div className="flex flex-row gap-2">
                <div className="p-card-title-header">{title}</div>
                {titleHint ? (
                    <>
                        <Tooltip target="pi-question-circle" />
                        <i
                            className="pi pi-question-circle"
                            data-pr-tooltip={titleHint}
                        />
                    </>
                ) : null}
            </div>
            <div className="flex gap-4">
                {actionsNode}
                {(!actions && canFold) || showCollapseButton ? (
                    <Button
                        className={collapsibleButtonClass}
                        tertiary
                        outlined
                        icon={<Icon name="keyboard_arrow_down" />}
                        label={isExpanded ? 'Hide' : 'Show'}
                        iconPos="right"
                        onClick={() => {
                            setIsExpanded(!isExpanded);
                        }}
                    />
                ) : null}
            </div>
        </div>
    );
    const templateCardTitle = typeof title !== 'string' ? title : textCardTitle;
    const cardTitle = Boolean(title)
        ? templateCardTitle ?? textCardTitle
        : null;
    const cardHeader = header ? (
        <div className={cardHeaderClass}>{header}</div>
    ) : null;

    const wrapperClass = clsx('w-full relative', wrapperClassName);
    return (
        <div data-testid={title} className={wrapperClass}>
            <LoadingBlur className="border-round-xl" loading={loading}>
                {cardHeader}
                <PrimeCard
                    onClick={onClick}
                    className={cardClass}
                    title={cardTitle}
                    subTitle={subTitle}
                    header={nativeHeader}
                    pt={pt}
                >
                    {className ? (
                        <div className={contentClass}>{children}</div>
                    ) : (
                        children
                    )}
                </PrimeCard>
            </LoadingBlur>
        </div>
    );
}

export const Card = forwardRef(CardComponent);
